import React, { useEffect, useRef, useState } from 'react';
import { KeyRound, MailCheck, ArrowLeft } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import axios from 'axios';
import { formatApiError } from '../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForgotPasswordForm = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try { await axios.post(`${API}/auth/forgot-password`, { email }); setSent(true); }
    catch (err) {
      const status = err?.response?.status;
      if (status === 429) setError('Too many requests. Please wait a few minutes and try again.');
      else if (status === 422) setError('Please enter a valid email address.');
      else setError("We couldn't process that request right now. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
      <button type="button" onClick={onBack} className="text-sm text-gray-500 flex items-center" data-testid="forgot-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back to sign in</button>
      <h3 className="text-lg font-semibold flex items-center"><KeyRound className="w-5 h-5 mr-2 text-red-500" />Forgot your password?</h3>
      {sent ? (
        <p className="text-sm text-green-700 bg-green-50 p-3 rounded" data-testid="forgot-sent-msg">If an account exists for that email, a reset link has been sent. Check your inbox (and spam folder).</p>
      ) : (
        <>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your account email" data-testid="forgot-email-input" />
          {error && <p className="text-sm text-red-600" data-testid="forgot-error">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white" data-testid="forgot-submit-btn">{busy ? 'Sending…' : 'Send reset link'}</Button>
        </>
      )}
    </form>
  );
};

export const ResetPasswordScreen = ({ token, onDone }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setBusy(true); setError('');
    try { await axios.post(`${API}/auth/reset-password`, { token, new_password: password }); setDone(true); }
    catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 p-4">
      <Card className="w-full max-w-md p-8 space-y-4" data-testid="reset-password-screen">
        <h2 className="text-xl font-bold flex items-center"><KeyRound className="w-5 h-5 mr-2 text-red-500" />Choose a new password</h2>
        {done ? (
          <>
            <p className="text-sm text-green-700 bg-green-50 p-3 rounded" data-testid="reset-success-msg">Your password has been updated. Sign in with your new password.</p>
            <Button className="w-full" onClick={onDone} data-testid="reset-go-login-btn">Go to sign in</Button>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (8+ chars, letters & numbers)" data-testid="reset-password-input" />
            <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" data-testid="reset-confirm-input" />
            {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white" data-testid="reset-submit-btn">{busy ? 'Updating…' : 'Update password'}</Button>
            <button type="button" onClick={onDone} className="w-full text-sm text-gray-500">Back to sign in</button>
          </form>
        )}
      </Card>
    </div>
  );
};

export const VerifyEmailScreen = ({ token, onDone }) => {
  const [state, setState] = useState('working');
  const [message, setMessage] = useState('');
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    axios.post(`${API}/auth/verify-email`, { token })
      .then((r) => { setState('ok'); setMessage(r.data.message); })
      .catch((err) => { setState('fail'); setMessage(formatApiError(err)); });
  }, [token]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 p-4">
      <Card className="w-full max-w-md p-8 space-y-4 text-center" data-testid="verify-email-screen">
        <MailCheck className={`w-12 h-12 mx-auto ${state === 'fail' ? 'text-red-400' : 'text-green-500'}`} />
        <h2 className="text-xl font-bold">{state === 'working' ? 'Verifying…' : state === 'ok' ? 'Email verified' : 'Verification failed'}</h2>
        <p className="text-sm text-gray-600" data-testid="verify-email-message">{message}</p>
        <Button className="w-full" onClick={onDone} data-testid="verify-continue-btn">Continue</Button>
      </Card>
    </div>
  );
};

export const VerifyEmailBanner = ({ user, onResent }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  if (!user || user.email_verified || user.verification_required) return null;
  const resend = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/auth/resend-verification`); setMsg(r.data.message); onResent?.(); }
    catch (err) { setMsg(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-4 py-2 flex items-center justify-between" data-testid="verify-email-banner">
      <span>{msg || 'Please confirm your email address — check your inbox for the verification link.'}</span>
      <button onClick={resend} disabled={busy} className="ml-3 underline font-medium whitespace-nowrap" data-testid="resend-verification-btn">{busy ? 'Sending…' : 'Resend'}</button>
    </div>
  );
};

// Full-screen gate shown when REQUIRE_EMAIL_VERIFICATION is on and the account is not verified yet.
export const VerifyEmailGate = ({ user, onRefresh, onLogout }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [tone, setTone] = useState('info');
  const resend = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/auth/resend-verification`); setMsg(r.data.message); setTone('ok'); }
    catch (err) {
      setTone('err');
      setMsg(err?.response?.status === 429 ? (err?.response?.data?.detail || 'Too many requests. Please wait a few minutes before requesting another email.') : formatApiError(err));
    } finally { setBusy(false); }
  };
  const check = async () => {
    setBusy(true);
    const u = await onRefresh();
    if (u && !u.email_verified) { setTone('info'); setMsg("Not verified yet — open the link in your email, then tap \"I've verified\"."); }
    setBusy(false);
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 p-4">
      <Card className="w-full max-w-md p-8 space-y-5 text-center" data-testid="verify-email-gate">
        <BrandLogo variant="wings" className="h-36 sm:h-44 w-auto mx-auto drop-shadow-lg" sizes="(max-width: 640px) 180px, 220px" priority testId="verify-gate-logo" />
        <MailCheck className="w-10 h-10 mx-auto text-rose-500" />
        <h2 className="text-xl font-bold">Confirm your email to continue</h2>
        <p className="text-sm text-gray-600">
          We sent a verification link to <span className="font-medium" data-testid="verify-gate-email">{user?.email}</span>.
          Open it to unlock Discover, Matches and Messages.
        </p>
        {msg && <p className={`text-sm ${tone === 'err' ? 'text-red-600' : tone === 'ok' ? 'text-green-700' : 'text-gray-700'}`} data-testid="verify-gate-msg">{msg}</p>}
        <div className="space-y-2">
          <Button className="w-full" onClick={check} disabled={busy} data-testid="verify-gate-check-btn">I've verified — continue</Button>
          <Button variant="outline" className="w-full" onClick={resend} disabled={busy} data-testid="verify-gate-resend-btn">{busy ? 'Working…' : 'Resend verification email'}</Button>
          <Button variant="ghost" className="w-full" onClick={onLogout} data-testid="verify-gate-logout-btn">Sign out</Button>
        </div>
      </Card>
    </div>
  );
};
